import { Badge } from "@solomon-labs/design/components/ui";
import React from "react";

import type { AccountType } from "../../../hooks/useSolanaAddressVerification";

/**
 * `AccountType` lives in the shared verification hook
 * (`useSolanaAddressVerification`); re-exported so callers that import the
 * badge don't need a separate import for the type.
 */
export type { AccountType };

/** The Squads account kinds the Company Recipient Console's Add a Position
 *  flow detects, beside the verification hook's `AccountType`s. */
export type SquadsAccountType = "squads-multisig" | "squads-vault";

/** Every kind the badge can name. */
export type AccountBadgeType = AccountType | SquadsAccountType;

type BadgeColor = NonNullable<React.ComponentProps<typeof Badge>["color"]>;

/**
 * Type → DS Badge colour (the graph palette, `fill`). Wallet leads with the
 * dark olive (quinary) so the happy-path tag carries the most weight in a
 * row; the rest tell kinds apart without the status colours
 * (success/warning/error), which are for the state of an entity, not its
 * type. Both Squads kinds share septenary, the colour the retail enrollment
 * already gave the multisig badge.
 */
export const ACCOUNT_TYPE_BADGE_COLOR: Record<AccountBadgeType, BadgeColor> = {
    wallet: "quinary",
    token: "primary",
    program: "quaternary",
    new: "nonary",
    unknown: "tertiary",
    "squads-multisig": "septenary",
    "squads-vault": "septenary"
};

/** Short labels: the badge sits inline before an address or label, so
 *  brevity beats precision. The precise wording lives in the verification
 *  status copy beside it. */
const ACCOUNT_TYPE_BADGE_LABEL: Record<AccountBadgeType, string> = {
    wallet: "Wallet",
    token: "Token",
    program: "Program",
    new: "New",
    unknown: "Unknown",
    "squads-multisig": "Squads Account",
    "squads-vault": "Squads Vault"
};

export interface MultisigShape {
    threshold: number;
    memberCount: number;
}

/** The badge's text for a type, e.g. "Wallet", "Squads Account (2/3)",
 *  "Squads Vault (2/3)". */
export function accountTypeBadgeLabel(
    type: AccountBadgeType,
    multisig?: MultisigShape | null
): string {
    const label = ACCOUNT_TYPE_BADGE_LABEL[type];
    return (type === "squads-multisig" || type === "squads-vault") && multisig
        ? `${label} (${multisig.threshold}/${multisig.memberCount})`
        : label;
}

export type AccountTypeBadgeProps =
    | {
          type: AccountType;
          multisig?: never;
          trailing?: React.ReactNode;
          className?: string;
      }
    | {
          type: SquadsAccountType;
          /** Threshold and member count, shown as "(2/3)" after the label.
           *  Both Squads kinds take it: either address names the same
           *  multisig. */
          multisig?: MultisigShape | null;
          /** A node inside the badge after the label (the DS `Badge`
           *  `trailing` slot), e.g. the authorized-signers tooltip trigger. */
          trailing?: React.ReactNode;
          className?: string;
      };

/**
 * What kind of Solana account an address is, as a small DS `Badge`: a
 * wallet, token account, program, unused (new) or unknown address, or a
 * Squads multisig (with its threshold) or Squads vault. Domain Component:
 * the mapping is YaaS's; the chip is the design system's.
 */
export function AccountTypeBadge({
    type,
    multisig,
    trailing,
    className
}: AccountTypeBadgeProps): React.ReactElement {
    return (
        <Badge
            color={ACCOUNT_TYPE_BADGE_COLOR[type]}
            className={className}
            trailing={trailing}
            data-account-type={type}
        >
            {accountTypeBadgeLabel(type, multisig)}
        </Badge>
    );
}
